import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {VisuallyHidden} from '@radix-ui/react-visually-hidden';
import {XIcon} from 'lucide-react';
import {cn} from '../lib/cn';
import {
  useCombinedRef,
  useIframeFocusTrapFallback,
  useIframeOpenAutoFocusGuard,
} from '../hooks';
import {getCustomContainer} from '../lib/utils';

/**
 * Radix Dialog's TitleWarning uses document.getElementById to check for a title,
 * which can miss iframe-hosted SDK documents. This mirrors the source package
 * workaround by adding a hidden title in the main document when needed.
 */
function useIframeTitleFix(titleRef: React.RefObject<HTMLHeadingElement | null>) {
  React.useEffect(() => {
    if (!window.chekinCustomDocument) {
      return;
    }

    const titleId = titleRef.current?.id;
    if (!titleId) return;

    const hiddenTitle = document.createElement('span');
    hiddenTitle.id = titleId;
    hiddenTitle.style.display = 'none';
    document.body.appendChild(hiddenTitle);

    return () => {
      hiddenTitle.remove();
    };
  }, [titleRef]);
}

function Dialog({...props}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({...props}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({...props}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({...props}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

const dialogOverlayClasses =
  'fixed inset-0 z-50 bg-[var(--dialog-overlay-bg)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0';

const scrollableOverlayClasses =
  'fixed inset-0 z-50 flex flex-col items-center overflow-y-auto overscroll-none pb-[19px] pt-[20px]';

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({className, ...props}, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    data-slot="dialog-overlay"
    className={cn(dialogOverlayClasses, className)}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const dialogContentClasses =
  'relative z-50 my-auto w-full max-w-[calc(100%-2rem)] rounded-[var(--dialog-content-radius)] border border-[var(--dialog-content-border)] bg-[var(--dialog-content-bg)] p-6 text-[var(--dialog-content-text)] shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:max-w-2xl';

const dialogCloseButtonClasses =
  'absolute right-5 top-4 flex size-6 items-center justify-center rounded-[var(--dialog-close-radius)] transition-opacity hover:opacity-70 focus:outline-none focus:shadow-chekin-focus disabled:pointer-events-none [&_svg:not([class*=size-])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0';

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    showCloseButton?: boolean;
    container?: HTMLElement;
    lockScroll?: boolean;
    overlayClassName?: string;
  }
>(
  (
    {
      className,
      showCloseButton = true,
      children,
      container,
      lockScroll = true,
      overlayClassName,
      ...props
    },
    ref,
  ) => {
    const contentRef = React.useRef<HTMLDivElement>(null);
    const combinedRef = useCombinedRef(contentRef, ref);
    const handleKeyDown = useIframeFocusTrapFallback(contentRef, props.onKeyDown);
    const handleOpenAutoFocus = useIframeOpenAutoFocusGuard(props.onOpenAutoFocus);

    const contentElement = (
      <DialogPrimitive.Content
        ref={combinedRef}
        data-slot="dialog-content"
        className={cn(dialogContentClasses, className)}
        {...props}
        onKeyDown={handleKeyDown}
        onOpenAutoFocus={handleOpenAutoFocus}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className={dialogCloseButtonClasses}
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    );

    const portalContainer = container || getCustomContainer();

    return (
      <DialogPortal data-slot="dialog-portal" container={portalContainer}>
        {lockScroll ? (
          <DialogOverlay
            className={cn(
              dialogOverlayClasses,
              scrollableOverlayClasses,
              overlayClassName,
            )}
          >
            {contentElement}
          </DialogOverlay>
        ) : (
          <div
            className={cn(
              dialogOverlayClasses,
              scrollableOverlayClasses,
              overlayClassName,
            )}
          >
            {contentElement}
          </div>
        )}
      </DialogPortal>
    );
  },
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({className, ...props}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    data-slot="dialog-header"
    className={cn('flex flex-col gap-2 text-center sm:text-left', className)}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({className, ...props}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    data-slot="dialog-footer"
    className={cn(
      'flex flex-col-reverse gap-[var(--dialog-footer-gap)] sm:flex-row sm:justify-end',
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({className, ...props}, ref) => {
  const titleRef = React.useRef<HTMLHeadingElement>(null);
  const combinedRef = useCombinedRef(titleRef, ref);

  useIframeTitleFix(titleRef);

  return (
    <DialogPrimitive.Title
      ref={combinedRef}
      data-slot="dialog-title"
      className={cn(
        'text-[length:var(--dialog-title-font-size)] font-[var(--dialog-title-font-weight)] leading-none',
        className,
      )}
      {...props}
    />
  );
});
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({className, ...props}, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    data-slot="dialog-description"
    className={cn('text-sm text-[var(--dialog-description-text)]', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

const DialogVisuallyHidden = ({children}: {children: React.ReactNode}) => (
  <VisuallyHidden>{children}</VisuallyHidden>
);
DialogVisuallyHidden.displayName = 'DialogVisuallyHidden';

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogVisuallyHidden,
};
